import { useState, useEffect } from "react";
import type { NextPage } from "next";
import Head from "next/head";
import {
  signup,
  SignupFormData,
  validatePassword,
  validateEmail,
  validateMobile,
  verifyOTP,
  resendOTP,
} from "../services/signupService";
import AuthForm, { AuthFormData } from "../components/common/AuthForm";
import VerificationModal, {
  VerificationType,
} from "../components/verifications";
import SignUpIn from "../components/signUp-In";
import {
  trackSignupPageView,
  trackSignupOtpRequested,
  trackSignupOtpAbandoned,
  trackSignupCompleted,
  trackSignupError,
  UtmParams,
} from "../lib/analytics";

const getSignupQueryParams = (): UtmParams => {
  if (typeof window === "undefined") return {};

  const queryParams: UtmParams = {};
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (value) queryParams[key] = value;
  });
  return queryParams;
};

const SignupPage: NextPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  const [isResumedVerification, setIsResumedVerification] = useState(false);

  // Tracks whether OTP was successfully verified in this session.
  // Used in handleModalClose to distinguish abandoned vs completed flows.
  const [otpVerified, setOtpVerified] = useState(false);

  // Signup query params lifted to state so all downstream events
  // (otp_requested, otp_abandoned, signup_completed) carry attribution.
  const [capturedUtmParams, setCapturedUtmParams] = useState<UtmParams>({});

  // 1. signup_page_view — fires once on mount
  useEffect(() => {
    const queryParams = getSignupQueryParams();
    setCapturedUtmParams(queryParams);
    trackSignupPageView(queryParams);
  }, []);

  const validateForm = (formData: AuthFormData): boolean => {
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setMessage({ type: "error", text: emailValidation.message! });
      return false;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setMessage({ type: "error", text: passwordValidation.message! });
      return false;
    }

    const mobileValidation = validateMobile(formData.mobile || "");
    if (!mobileValidation.isValid) {
      setMessage({ type: "error", text: mobileValidation.message! });
      return false;
    }

    return true;
  };

  const handleSubmit = async (formData: AuthFormData) => {
    if (!validateForm(formData)) return;

    setIsLoading(true);
    const utmParams = getSignupQueryParams();
    setCapturedUtmParams(utmParams);

    try {
      const signupData: SignupFormData = {
        email: formData.email,
        password: formData.password,
        mobile: formData.mobile || "",
        company: "untitled",
        utmParams,
      };

      const response = await signup(signupData);

      if (response.success) {
        setMessage({ type: "success", text: response.message });
        setUserEmail(formData.email);
        setUserMobile(formData.mobile || "");
        setIsResumedVerification(Boolean(response.resumedPendingVerification));

        // 2. signup_otp_requested — account created, OTP modal about to show.
        //    UTM params included so campaign attribution is visible here too.
        trackSignupOtpRequested(
          formData.email,
          formData.mobile || "",
          utmParams
        );

        setShowVerificationModal(true);
      } else {
        setIsResumedVerification(false);
        setMessage({ type: "error", text: response.message });
        trackSignupError(undefined, response.message, utmParams);
      }
    } catch (error) {
      const errMsg = "An unexpected error occurred. Please try again.";
      setMessage({ type: "error", text: errMsg });
      trackSignupError("unexpected_error", errMsg, utmParams);
    } finally {
      setIsLoading(false);
    }
  };

  // Fires when the OTP modal is closed via the X / backdrop.
  // If OTP hasn't been verified yet → user abandoned at this step.
  const handleModalClose = () => {
    if (!otpVerified) {
      trackSignupOtpAbandoned(userEmail, userMobile, capturedUtmParams);
    }
    setShowVerificationModal(false);
    setIsResumedVerification(false);
  };

  // Redirect to index — AuthProvider will kick off OIDC login from there.
  const handleLoginRedirect = () => {
    window.location.assign("/");
  };

  const handlePhoneEdit = () => {
    setShowVerificationModal(false);
    setMessage({
      type: "error",
      text: "Please update your mobile number and try again.",
    });
  };

  const handleVerificationSuccess = async (otp: string) => {
    try {
      const verificationResponse = await verifyOTP(userEmail, otp);

      if (verificationResponse.success) {
        setMessage({
          type: "success",
          text: isResumedVerification
            ? "Account verified! Sign in with the password from your first signup attempt."
            : "Account verified! Redirecting to sign in...",
        });

        // Mark as verified before closing — prevents handleModalClose
        // from firing signup_otp_abandoned on the subsequent state update.
        setOtpVerified(true);
        setShowVerificationModal(false);
        setIsResumedVerification(false);

        // 3. signup_completed — OTP verified, account fully confirmed.
        //    Full attribution payload: email + mobile + all UTM/Ads params.
        trackSignupCompleted(userEmail, userMobile, capturedUtmParams);

        // Account is confirmed — hard-navigate home so OIDC login kicks in.
        setTimeout(() => {
          window.location.assign("/");
        }, 2000);
        return Promise.resolve();
      } else {
        setMessage({ type: "error", text: verificationResponse.message });
        trackSignupError(
          "otp_invalid",
          verificationResponse.message,
          capturedUtmParams
        );
        return Promise.reject(new Error(verificationResponse.message));
      }
    } catch (error: any) {
      const errorMessage =
        error.message || "Verification failed. Please try again.";
      setMessage({ type: "error", text: errorMessage });
      trackSignupError("otp_error", errorMessage, capturedUtmParams);
      return Promise.reject(error);
    }
  };

  const handleResendOTP = async () => {
    if (isResendingOTP) return;

    setIsResendingOTP(true);
    try {
      const response = await resendOTP(userEmail);

      if (response.success) {
        setMessage({ type: "success", text: response.message });
        setTimeout(() => {
          setMessage(null);
        }, 5000);
      } else {
        setMessage({ type: "error", text: response.message });
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text:
          error.message ||
          "Failed to resend verification code. Please try again.",
      });
    } finally {
      setIsResendingOTP(false);
    }
  };

  return (
    <>
      <Head>
        <title>Sign Up — OneCap</title>
      </Head>

      <div className="signup-shell">
        {/* Left panel — hero (hidden at ≤ 960px via .signup-hero CSS) */}
        <div className="signup-hero">
          <SignUpIn />
        </div>

        {/* Right panel — form */}
        <div className="signup-form-panel">
          <AuthForm
            onSubmit={handleSubmit}
            isLoading={isLoading}
            message={message}
            onRedirect={handleLoginRedirect}
          />
        </div>
      </div>

      {showVerificationModal && (
        <VerificationModal
          onClose={handleModalClose}
          verificationType={VerificationType.PhoneNumber}
          verificationValue={userMobile}
          onValueEdit={handlePhoneEdit}
          onVerificationSuccess={handleVerificationSuccess}
          onResendOTP={handleResendOTP}
          isResendingOTP={isResendingOTP}
          isResumedVerification={isResumedVerification}
        />
      )}
    </>
  );
};

export default SignupPage;
